import { Spinner } from '@repo/ui'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect } from 'react'

import { OnboardingLayout } from '@/components/onboarding/onboarding-layout'
import { StepCheckout } from '@/components/onboarding/step-checkout'
import { StepHours } from '@/components/onboarding/step-hours'
import { StepMembers } from '@/components/onboarding/step-members'
import { StepOrgCreation } from '@/components/onboarding/step-org-creation'
import { StepPlan } from '@/components/onboarding/step-plan'
import { StepSectors } from '@/components/onboarding/step-sectors'
import { StepWhatsapp } from '@/components/onboarding/step-whatsapp'
import { useAuth } from '@/hooks/use-auth'
import { useOnboardingState } from '@/hooks/use-onboarding'
import { authApi } from '@/lib/api'
import type { SessionResponse } from '@/lib/api-types'
import {
  ONBOARDING_STEPS,
  type OnboardingStepDef,
  type OnboardingStepStatus,
} from '@/types/onboarding'

type OnboardingSearch = {
  step?: number
  session_id?: string
  new?: boolean
}

export const Route = createFileRoute('/onboarding')({
  validateSearch: (search: Record<string, unknown>): OnboardingSearch => ({
    step:
      typeof search.step === 'string'
        ? Number(search.step)
        : typeof search.step === 'number'
          ? search.step
          : undefined,
    session_id:
      typeof search.session_id === 'string' ? search.session_id : undefined,
    new: search.new === true || search.new === 'true',
  }),
  component: OnboardingPage,
})

function OnboardingPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/onboarding' })
  const { user, loading, currentOrgId, refresh } = useAuth()
  // Em "nova org", ignoramos a org ativa para não puxar o estado dela.
  const isNewOrg = search.new === true
  const effectiveOrgId = isNewOrg ? null : currentOrgId
  const { data: onboardingState, isLoading: stateLoading } =
    useOnboardingState(effectiveOrgId)
  const currentStep = Math.min(
    7,
    Math.max(
      1,
      search.step ?? (isNewOrg ? 1 : (onboardingState?.currentStep ?? 1))
    )
  )
  const activeStep = ONBOARDING_STEPS.find(
    (step) => step.stepNumber === currentStep
  )

  useEffect(() => {
    if (loading) return
    if (!user) {
      void confirmSessionBeforeRedirect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user])

  async function confirmSessionBeforeRedirect() {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const { data } = await authApi.get<SessionResponse>(
          '/api/auth/get-session'
        )
        if (data.user) {
          await refresh()
          return
        }
      } catch {
        // Keep the retry quiet; redirect below if the session is truly absent.
      }

      await new Promise((resolve) => window.setTimeout(resolve, 150))
    }

    void navigate({ to: '/entrar', search: { returnTo: '/onboarding' } })
  }

  function getStepStatus(step: OnboardingStepDef): OnboardingStepStatus {
    if (!onboardingState) return step.stepNumber === 1 ? 'active' : 'pending'
    if (step.stepNumber < currentStep) return 'complete'
    if (step.stepNumber === currentStep) return 'active'
    return 'pending'
  }

  function goToStep(step: number) {
    void navigate({ to: '/onboarding', search: { step } })
  }

  if (loading || !user || (effectiveOrgId && stateLoading)) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <Spinner className="text-muted-foreground h-6 w-6" />
      </div>
    )
  }

  const stepComponents: Record<number, React.ReactNode> = {
    1: (
      <StepOrgCreation
        orgId={effectiveOrgId}
        forceCreate={isNewOrg}
        onNext={async () => {
          await refresh()
          goToStep(2)
        }}
      />
    ),
    2: (
      <StepPlan orgId={currentOrgId} onCreateOrganization={() => goToStep(1)} />
    ),
    3: (
      <StepCheckout
        orgId={currentOrgId}
        sessionId={search.session_id}
        onBack={() => goToStep(2)}
        onNext={() => goToStep(4)}
      />
    ),
    4: (
      <StepWhatsapp
        orgId={currentOrgId}
        onNext={() => goToStep(5)}
        onSkip={() => goToStep(5)}
      />
    ),
    5: (
      <StepHours
        orgId={currentOrgId}
        onNext={() => goToStep(6)}
        onSkip={() => goToStep(6)}
      />
    ),
    6: (
      <StepSectors
        orgId={currentOrgId}
        onNext={() => goToStep(7)}
        onSkip={() => goToStep(7)}
      />
    ),
    7: (
      <StepMembers
        orgId={currentOrgId}
        onNext={() => void navigate({ to: '/inbox' })}
        onSkip={() => void navigate({ to: '/inbox' })}
      />
    ),
  }

  return (
    <OnboardingLayout
      title={activeStep?.label ?? 'Configuração'}
      subtitle={activeStep?.description}
      steps={ONBOARDING_STEPS}
      currentStep={currentStep}
      getStepStatus={getStepStatus}
      onNavigateToStep={goToStep}
    >
      {stepComponents[currentStep] ?? (
        <StepPlan
          orgId={currentOrgId}
          onCreateOrganization={() => goToStep(3)}
        />
      )}
    </OnboardingLayout>
  )
}
